<template lang="pug">
  y-dialog(v-model="showDialog" :fullscreen="$vuetify.breakpoint.xsOnly" :max-width="maxWidth" persistent scrollable content-class="round-3xl")
    v-card
      v-card-title.pa-4
        v-row(no-gutters align="center")
          v-col(cols="10" offset="1")
            div.text-xl.font-bold.neutral700--text.text-center {{ $t('create_input_field') }}
          v-col(cols="1" align="right")
            v-btn.ml-n3(text icon @click="closeDialog")
              v-icon(color="neutral700" :size="24") mdi-close

      v-divider
      v-card-text(:class="$vuetify.breakpoint.xsOnly ? 'pa-4' : 'pa-8'")
        v-form(ref="form")
          v-row(no-gutters)
            v-col
              div.text-md.font-semibold.neutral700--text {{ $t('fill_the_information_below') }}
              .form-input
                div.text-sm.font-semibold.neutral500--text.mt-4 {{ $t('input_type') }}
                v-select.text-sm.mt-2.round-xl(v-model="form.type" :items="types" :placeholder="$t('select_input_type')" item-text="text" item-value="value" outlined :rules="rules.not_empty_select" hide-details="auto" dense :disabled="form.default" :class="form.default ? 'basic400' : ''")
                div.text-sm.font-semibold.neutral500--text.mt-4 {{ $t('title') }}
                v-text-field.text-sm.mt-2.round-xl(v-model="form.name" :placeholder="$t('enter_title')" outlined :rules="rules.not_empty" hide-details="auto" dense)
                div(v-if="hasPlaceholder")
                  div.text-sm.font-semibold.neutral500--text.mt-4 {{ $t('placeholder') }} ({{ $t('optional') }})
                  v-text-field.text-sm.mt-2.round-xl(v-model="form.placeholder" outlined :placeholder="$t('enter_placeholder')" :hint="$t('placeholder_desc')" persistent-hint dense)
                .mt-6.text-md.font-semibold.neutral700--text {{ $t('settings') }}
                v-row.mt-2(no-gutters)
                  v-col(:cols="form.type === 'selection' ? 6 : 5")
                    div.mb-3.text-sm.neutral700--text {{ $t('make_required') }}
                    div.mb-3.text-sm.neutral700--text(v-if="form.type === 'selection'") {{ $t('allow_multiple_selection') }}
                  v-col
                    y-checkbox.mb-1(v-model="form.required" :disabled="disabledRequiredCheckbox")
                    y-checkbox.mb-1(v-if="form.type === 'selection'" v-model="form.allow_multiple_selection")
            template(v-if="form.type === 'selection'")
              v-col.mx-6.shrink
                v-divider(vertical)
              v-col
                .list-answer
                  div.text-sm.font-semibold.neutral500--text.mt-4 {{ $t('list_of_answers') }}
                  v-row.mt-2(v-for="(choice, cindex) in choices" no-gutters :key="'choice-' + cindex")
                    v-col
                      v-text-field.text-sm.round-xl(v-model="choice.text" :placeholder="$t('enter_answer')" outlined :rules="rules.not_empty" hide-details="auto" dense)
                    v-col.shrink(v-if="cindex !== 0")
                      v-btn.ml-3(icon small @click="removeItem(choice)" style="margin-top: 6px")
                        v-icon(color="neutral700") $yzi-trash-03
                  v-btn.button-xs.mt-2(color="neutral700" outlined @click="addItem")
                    v-icon(color="neutral700" left) $yzi-plus
                    | {{ $t('add_answer') }}

      v-divider
      v-card-actions.py-4(:class="$vuetify.breakpoint.xsOnly ? 'px-4' : 'px-8'")
        v-btn.button-md.neutral700--text.px-0.underline(@click="closeDialog" text) {{ $t('cancel') }}
        v-spacer
        v-btn.button-md(:loading="isLoading" color="neutral700" depressed dark @click="saveData") {{ $t('save') }}

</template>

<script>
import { mapGetters, mapActions } from 'vuex'

export default {
  name: 'EditFeedbackFormDialog',
  props: {
    open: {
      type: Boolean,
    },
    field: {
      type: Object,
      default: () => {
        return {}
      }
    },
    fieldIndex: {
      type: Number,
      default: null
    },
    allFields: {
      type: Array,
      default: () => {
        return []
      }
    }
  },
  data () {
    return {
      isLoading: false,
      form: {},
      choices: [{ text: null }],
      rules: {
        not_empty: [val => (val || '').length > 0 || 'This field is required'],
        has_quantity: [v => !!v || 'This field is required', v => v != 0 || 'This field is required'],
        has_price: [(v) => !!v || 'This field is required', (v) => v >= 1|| `Amount should be more than ${this.$currency.symbol} 1`],
        not_empty_select: [v => !!v || 'This field is required']
      },
    }
  },
  computed: {
    ...mapGetters({
      company: 'getCompany',
    }),
    showDialog: {
      get () {
        return this.open
      },
      set (val) {
        this.$emit('update:open', val)
      }
    },
    types() {
      return [
        { text: 'Short answer', value: 'text' },
        { text: 'Multiple choice', value: 'selection' },
        { text: 'Text area field', value: 'long_text' },
        { text: 'Number only', value: 'number' },
        { text: 'Date', value: 'date' },
        { text: 'Phone number', value: 'phone' },
        { text: 'Email', value: 'email', disabled: this.isFieldDisabled('email') },
        { text: 'Address', value: 'address', disabled: this.isFieldDisabled('address') },
      ]
    },
    hasPlaceholder() {
      const hasPlaceholders = ['text', 'long_text', 'number', 'email', 'date']
      return hasPlaceholders.includes(this.form.type)
    },
    maxWidth() {
      if (this.form.type === 'selection') {
        return 900
      }
      return 600
    },
    disabledRequiredCheckbox() {
      if (this.form.default) {
        const optionalDefaultTypes = ['email', 'address']
        if (optionalDefaultTypes.includes(this.form.type)) {
          return false
        }
        return true
      }
      return false
    },
    emailDisabled() {
      let hasEmailField = false
      if (this.field) {
        hasEmailField = this.allFields.filter((field, index) => field.type === 'email' && this.fieldIndex !== index).length > 0
      } else {
        hasEmailField = this.allFields.filter(field=> field.type === 'email').length > 0
      }
      return hasEmailField
    },
  },
  mounted () {
    this.setData()
  },
  methods: {
    ...mapActions(['showMessage']),
    setData () {
      this.form = { ...this.field }
      if (this.form.type === 'radiobtn' || this.form.type === 'checklist') {
        this.form.allow_multiple_selection = this.form.type === 'checklist'
        this.choices = this.form.choices.map(m => { return { text: m } })
        this.form.type = 'selection'
      }
    },
    async saveData () {
      if (!this.$refs.form.validate()) {
        return
      }
      this.isLoading = true
      let form = { ...this.form }
      form.placeholder = this.form.placeholder ?? null
      if (this.form.type === 'selection') {
        form.choices = this.choices.map(m => m.text)
        form.type = this.form.allow_multiple_selection ? 'checklist' : 'radiobtn'
      }
      try {
        const { data } = this.fieldIndex || this.fieldIndex === 0 ? await this.$axios.patch(`/reviews/${this.company.slug}/form_fields/${this.fieldIndex}`, form) : await this.$axios.post(`/reviews/${this.company.slug}/form_fields`, form)
        this.$emit('updated', data)
        this.showMessage(['success', this.$t('success_message.update_successful')])
        this.closeDialog()
      } catch (error) {
        this.showMessage(['error', this.$t('error_message.unable_to_complete_request')])
      } finally {
        this.isLoading = false
      }
    },
    addItem () {
      this.choices.push({ text: null })
    },
    removeItem (choice) {
      this.choices = this.choices.filter(f => f !== choice)
    },
    closeDialog () {
      this.showDialog = false
      this.$emit('closed')
    },
    isFieldDisabled(fieldType) {
      let hasExistingField = false
      if (this.field) {
        hasExistingField = this.allFields.filter((field, index) => field.type === fieldType && this.fieldIndex !== index).length > 0
      } else {
        hasExistingField = this.allFields.filter(field=> field.type === fieldType).length > 0
      }
      return hasExistingField
    }
  }
}
</script>

<style scoped>
.v-text-field--outlined >>> fieldset {
  border-color: var(--v-basic500);
}
.error--text >>> fieldset {
  border-color: var(--v-danger);
}
</style>